import { isPrnText } from '#src/models/medicationDosage.ts'

// Reduces a free-text sig ("1 tablet orally 2 times per day with meals") to
// the dose and frequency a medication row shows next to the drug name
// ("1 tab BID"). Elation sends `directions` as prose, so this is a display
// summary, not a parse of a structured Dosage — anything it cannot recognize
// is dropped rather than guessed at, and the caller falls back to the drug
// name alone.
//
// Not a complete sig language. The patterns cover the production directions
// we have actually seen, and a regex will keep missing prose. Chasing every
// miss is not the plan. The durable version is a model that reads the sig
// into a dose and a frequency, accepted only when a comprehensive eval says
// the label is safe to show.
//
// Kept separate from ExtendedMedicationRequest.parseDailyDoseFromText, which
// answers a different question (how much per day, for supply estimates) and
// would have to trade its numeric accuracy for display abbreviations.

export interface SigSummary {
  // Dose amount as written: "1", "0.5", "1-2".
  amount?: string
  // Abbreviated dose form: tab, cap, gtt, supp, mL, patch, app, puff, spray.
  form?: string
  // Abbreviated frequency: QD, BID, TID, QID, Q6H, QOD, QHS, BIW, TIW, QW, once, weekly.
  frequency?: string
  asNeeded: boolean
}

const FORM_ABBREVIATIONS: [RegExp, string][] = [
  [/\btablets?\b|\btabs?\b|\bpills?\b/, 'tab'],
  [/\bcapsules?\b|\bcaps?\b/, 'cap'],
  [/\bsuppositor(?:y|ies)\b/, 'supp'],
  [/\bdrops?\b|\bgtts?\b/, 'gtt'],
  [/\bpatch(?:es)?\b/, 'patch'],
  [/\bapplications?\b/, 'app'],
  [/\bpuffs?\b/, 'puff'],
  [/\bsprays?\b/, 'spray'],
  [/\binhalations?\b/, 'inh'],
  [/\blozenges?\b/, 'loz'],
  [/\bscoops?\b/, 'scoop'],
  [/\bpackets?\b/, 'packet'],
  [/\bunits?\b/, 'unit'],
  [/\bml\b|\bmilliliters?\b/, 'mL'],
  [/\bmg\b|\bmilligrams?\b/, 'mg'],
  [/\bgrams?\b/, 'g'],
]

// "1 tablet daily" and "daily at bedtime" name the schedule. "for daily
// headaches" does not: daily followed by a noun is an indication.
const DAILY_RATE =
  /\bonce daily\b|\bevery day\b|\bqd\b|\bonce (?:per |a )?day\b|\bdaily\b(?=$|[^\sa-z]|\s+(?:at|with|before|after|for|and|or|on|in|as|if|except|then|when|prn)\b)/

// First explicit rate wins. Worded multipliers sit above bare "weekly" and
// "monthly", and "one time daily" sits above the single-dose pattern: a daily
// qualifier means the dose repeats.
const RATE_PATTERNS: [RegExp, string | ((m: RegExpMatchArray) => string)][] = [
  // The (?<![\d-]) guards stop a range ("1-2 times a day") from reading as the
  // count at its upper bound. A range has no single abbreviation, so it falls
  // through every pattern and the row shows the dose without a frequency.
  [
    /\bqid\b|(?<![\d-])(?<!to )(?<!or )(?<!and )(?<!\d - )(?:4|four) times (?:per |a |every )?(?:day|daily)\b|(?<![\d-])4\s*x\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b/,
    'QID',
  ],
  [
    /\btid\b|(?<![\d-])(?<!to )(?<!or )(?<!and )(?<!\d - )(?:3|three) times (?:per |a |every )?(?:day|daily)\b|(?<![\d-])3\s*x\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b/,
    'TID',
  ],
  [
    /\bbid\b|(?<![\d-])(?<!to )(?<!or )(?<!and )(?<!\d - )(?:2|two) times (?:per |a |every )?(?:day|daily)\b|\btwice(?:[-\s]+a)?[-\s]+(?:day|daily)\b|(?<![\d-])2\s*x\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b/,
    'BID',
  ],
  // "1 time per day" and "one time daily" are schedules. "one time" alone is
  // handled later and is a single dose.
  [/(?<![\d-])(?:1|one) times?\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b/, 'QD'],
  [/\bevery (\d+) hours?\b/, (m) => `Q${m[1]}H`],
  // Compact q6h / q5h. q0h is not a schedule.
  [
    /\bq\s*(\d+)\s*h(?:ours?|rs?)?\b/,
    (m) => {
      const hours = Number(m[1])
      return hours > 0 ? `Q${hours}H` : ''
    },
  ],
  // "every other morning" is every other day, not a daily morning dose.
  [/\bevery other (?:day|morning|evening|night)\b|\bqod\b/, 'QOD'],
  [/\bevery (\d+) days?\b/, (m) => `Q${m[1]}D`],
  [/(?<![\d-])(\d+) times (?:per |a )?week(?:ly)?\b/, (m) => `${m[1]}x/week`],
  [
    /(?<![\d-])(\d+)x\s+(?:(?:per|a)\s+)?(?:week|weekly)\b/,
    (m) => boundedPeriodCount(m[1], 'week'),
  ],
  [
    /\b(?:twice|two times)(?:[-\s]+(?:per|a))?[-\s]+(?:week|weekly)\b/,
    '2x/week',
  ],
  [/\bthree times (?:(?:per |a )?(?:week|weekly))\b/, '3x/week'],
  [/\bfour times (?:(?:per |a )?(?:week|weekly))\b/, '4x/week'],
  // Written abbreviations. "twice weekly" stays "2x/week"; these are the
  // sigs that already use the short form.
  [/\bbiw\b/, 'BIW'],
  [/\btiw\b/, 'TIW'],
  [/\bqw\b/, 'QW'],
  // "1 tablet weekly" is a schedule. "for weekly headaches" is not: weekly
  // followed by a noun has no administration phrase, same as bare daily.
  [
    /\bonce weekly\b|\bweekly\b(?=$|[^\sa-z]|\s+(?:[^\sa-z]|\d+\b|at\b|with\b|before\b|after\b|for\b|and\b|or\b|on\b|in\b|as\b|if\b|except\b|then\b|when\b|prn\b))|\bonce (?:per|a) week\b/,
    'weekly',
  ],
  [
    /(?<![\d-])(\d+)\s+times?\s+(?:(?:per|a)\s+)?month(?:ly)?\b/,
    (m) => `${m[1]}x/month`,
  ],
  [
    /(?<![\d-])(\d+)x\s+(?:(?:per|a)\s+)?(?:month|monthly)\b/,
    (m) => boundedPeriodCount(m[1], 'month'),
  ],
  [
    /\b(?:twice|two times)(?:[-\s]+(?:per|a))?[-\s]+(?:month|monthly)\b/,
    '2x/month',
  ],
  [/\bthree times (?:(?:per |a )?(?:month|monthly))\b/, '3x/month'],
  [/\bfour times (?:(?:per |a )?(?:month|monthly))\b/, '4x/month'],
  // "1 tablet monthly" is a schedule. "for monthly migraines" is not.
  [
    /\bonce monthly\b|\bmonthly\b(?=$|[^\sa-z]|\s+(?:[^\sa-z]|\d+\b|at\b|with\b|before\b|after\b|for\b|and\b|or\b|on\b|in\b|as\b|if\b|except\b|then\b|when\b|prn\b))|\bonce (?:per|a) month\b/,
    'monthly',
  ],
  // "one time at bedtime" is a single dose given at night, not a nightly
  // schedule. The lookahead keeps "per", "a", and a daily qualifier from
  // reading as one dose.
  [
    /\bone time\b(?!\s+(?:per\b|a\b|daily\b|every\s+day\b))|\bonce only\b/,
    'once',
  ],
  [DAILY_RATE, 'QD'],
]

// Clock times are not rates. Two or more distinct ones are the schedule.
const TIME_OF_DAY_PATTERNS: [RegExp, string][] = [
  [
    /\b(?:qam|every morning|each morning|in the morning|(?:and|,)\s+morning|morning\s+and|morning(?=\s*,))\b/,
    'QAM',
  ],
  [
    /\b(?:qpm|every evening|each evening|in the evening|(?:and|,)\s+evening|evening\s+(?:and|,))\b/,
    'QPM',
  ],
  [
    /\b(?:at bedtime|qhs|before beds?|before bedtime|(?:and|,)\s+bedtime|at nighttime|at night|every night|each night|in the night|(?:and|,)\s+night|night\s+(?:and|,)|nightly(?=$|[^\sa-z]|\s+(?:[^\sa-z]|\d+\b|at\b|with\b|before\b|after\b|for\b|and\b|or\b|on\b|in\b|as\b|if\b|except\b|then\b|when\b|prn\b)))\b/,
    'QHS',
  ],
]

const TIME_LABELS = new Set(TIME_OF_DAY_PATTERNS.map(([, label]) => label))

// "1 or 2 times", "one to two times", "twice to three times", "once to
// twice", "1 - 2 times", "once or twice", and "2-3x" / "2 to 3x" daily,
// weekly, or monthly have no single rate. A bare "daily" or "weekly" after
// that range is part of the range, not a standing schedule.
const TIMES_RANGE =
  /\b(?:once|twice|\d+|one|two|three|four)\s*(?:-\s*|to\s+|or\s+|and\s+)(?:once|twice|\d+|one|two|three|four)\s+times\b|\b(?:once|one)\s+(?:-\s*|to\s+|or\s+)twice\b|\btwice\s+(?:-\s*|to\s+|or\s+)(?:once|one)\b|\b\d+\s*(?:-|to|or)\s*\d+\s*x\s+(?:(?:per|a)\s+)?(?:day|daily|week(?:ly)?|month(?:ly)?)\b/

const RANGED_RATES = new Set(['QD', 'BID', 'TID', 'QID'])

// mg/g/mL are usually the product strength. Use them only when no dose form
// (tablet, capsule, and so on) matched.
const STRENGTH_FORMS = new Set(['mg', 'g', 'mL'])

// A ceiling is not the dose. Drop the phrase and the quantity it governs so
// "do not exceed 4 tablets daily" cannot become 4 tab QD, and cannot override
// a schedule stated beside it. A colon or equals after the cue ("MDD: 4",
// "MDD = 4") is the same ceiling. "maximum two tablets" is a ceiling too.
const WORDED_COUNT =
  'one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve'
const CEILING_NUMBER = `(?:\\d+\\s+\\d+\\/\\d+|\\d+\\/\\d+|\\d{1,3}(?:,\\d{3})+(?:\\.\\d+)?|\\d+(?:\\.\\d+)?|\\.\\d+|\\b(?:${WORDED_COUNT})\\b)`
const CEILING_AMOUNT = `${CEILING_NUMBER}(?:\\s*(?:-|to|or)\\s*${CEILING_NUMBER})?`
const CEILING_FREQUENCY = '(?:once|twice)(?:\\s+(?:daily|(?:a|per)\\s+day))?'
const CEILING = new RegExp(
  `\\b(?:do not exceed|should not exceed|must not exceed|do not take more than|not to exceed|not more than|no more than|up to|max(?:imum)?|mdd)(?:\\s*[: =]\\s*|\\s+)(?:[a-z]+\\s+){0,3}(?:${CEILING_AMOUNT}|${CEILING_FREQUENCY})(?:\\s+(?:tablets?|tabs?|capsules?|caps?|pills?|daily|day|days|a|per|times?|orally|mg|mls?|milliliters?|milligrams?|doses?)\\b){0,5}`,
  'g'
)

// "1", "0.5", ".5", "1/2", "1 1/2", "1 and 1/2", "1-1/2", "1-2", "1 to 2",
// "1/2 to 3/4", "1/2 to 1 1/2", "1 1/2 to 2/3", "1/4-1/2", "1 1/2 to 2 1/2",
// "1 to 1 1/2", "1 or 2", or a grouped "1,000" / "1,250", each followed by
// the form. The bare-leading-dot form matters: ".5 tablet" read as "5"
// overstates the dose tenfold. A fraction-to-fraction range is first so
// "1/2 to 3/4" is not the upper "3/4" and "1/4-1/2" is not the trailing
// "4-1/2". A fraction into a mixed number is next so "1/2 to 1 1/2" is not
// the trailing "1 1/2". A mixed number into a fraction follows so
// "1 1/2 to 2/3" is not the trailing "2/3". A mixed-to-mixed range is next
// so "1 1/2 to 2 1/2" is not the trailing "2 1/2". A mixed number into a
// plain number is next so "1 1/2 to 2" is not the upper "2". A range into a
// mixed number follows so "1 to 1 1/2" is not the trailing "1 1/2". The
// mixed-number alternative is next so "1 1/2" and "1-1/2" are not the
// trailing "1/2". "or" is a range, same as "to", so "1 or 2 tablets" is not
// the upper number alone. Grouped digits come before a plain run of digits
// so "1,000" is not resumed as "000".
const AMOUNT_AND_FORM =
  /((?:\d+\s+or\s+\d+\/\d+)|(?:\d+\.\d+\s*(?:to|or|-)\s*\d+\/\d+)|(?:\d+\/\d+\s*(?:to|or|-)\s*\d+\/\d+)|(?:\d+\/\d+\s*(?:to|or|-)\s*\d+\s+\d+\/\d+)|(?:\d+\s+\d+\/\d+\s+(?:to|or|-)\s*\d+\/\d+)|(?:\d+\s+\d+\/\d+\s+(?:to|or|-)\s+\d+\s+\d+\/\d+)|(?:\d+\s+\d+\/\d+\s+(?:to|or|-)\s+\d+(?:\.\d+)?)|(?:\d+(?:\.\d+)?\s+(?:to|or|-)\s+\d+\s+\d+\/\d+)|(?:\d+(?:\s+(?:and\s+)?|\s*-\s*)\d+\/\d+)|(?:(?:\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?|\.\d+)(?:\/\d+)?(?:\s*(?:-|to|or)\s*(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)?))\s*([a-z]+)/

function withoutCeilings(lower: string): string {
  return lower.replace(CEILING, ' ').replace(/\s+/g, ' ').trim()
}

function matchRate(lower: string): string | undefined {
  const ranged = TIMES_RANGE.test(lower)
  for (const [pattern, abbreviation] of RATE_PATTERNS) {
    const match = lower.match(pattern)
    if (!match) {
      continue
    }
    const label =
      typeof abbreviation === 'string' ? abbreviation : abbreviation(match)
    if (!label) {
      continue
    }
    if (
      ranged &&
      (RANGED_RATES.has(label) ||
        /^\d+x\//.test(label) ||
        label === 'weekly' ||
        label === 'monthly')
    ) {
      continue
    }
    return label
  }
  return undefined
}

function matchTimes(lower: string): string[] {
  const labels: string[] = []
  for (const [pattern, label] of TIME_OF_DAY_PATTERNS) {
    const administered = [
      ...lower.matchAll(new RegExp(pattern.source, 'g')),
    ].some(
      (match) => match.index != null && !clockTimeIsNotADose(lower, match.index)
    )
    if (administered && !labels.includes(label)) {
      labels.push(label)
    }
  }
  // "every evening at bedtime" is one dose. "in the evening and at bedtime"
  // is two: bedtime qualifies the same dose only when nothing separates them.
  if (
    labels.includes('QPM') &&
    labels.includes('QHS') &&
    eveningAndBedtimeAreOneDose(lower)
  ) {
    return [
      ...labels.filter((label) => label !== 'QPM' && label !== 'QHS'),
      'QD',
    ]
  }
  return labels
}

// "check blood pressure at night" is monitoring, not a second dose.
function clockTimeIsNotADose(lower: string, at: number): boolean {
  if (
    clockTimeIsMonitoring(lower, at) ||
    clockTimeIsInsideIndication(lower, at)
  ) {
    return true
  }
  const boundary = Math.max(
    lower.lastIndexOf(';', at),
    lower.lastIndexOf('.', at),
    lower.lastIndexOf(',', at)
  )
  const clause = lower.slice(boundary + 1, at)
  const nonDoseClause =
    /\b(?:avoid|causes?|side effects?)\b/.test(clause) ||
    /\bfor\s+(?:[a-z]+\s+){0,4}(?:at|in the|every|before)\b/.test(clause)
  if (!nonDoseClause) {
    return false
  }
  return !/\b(?:tablets?|tabs?|capsules?|caps?|pills?|drops?|puffs?|sprays?|patches?|take|give|inject|apply|inhale|instill)\b/.test(
    clause
  )
}

// "for pain in the morning" names when the pain happens. "in the morning"
// on its own is still the dose time.
function clockTimeIsInsideIndication(lower: string, at: number): boolean {
  const before = lower.slice(0, at)
  const fors = [...before.matchAll(/\bfor\b/g)]
  const last = fors.at(-1)
  if (last?.index == null) {
    return false
  }
  const between = before.slice(last.index)
  if (
    /\b(?:tablets?|tabs?|capsules?|caps?|pills?|drops?|puffs?|sprays?|patches?|take|give|inject|apply|inhale|instill)\b/.test(
      between
    )
  ) {
    return false
  }
  return /\bfor\s+(?:[a-z]+\s+){1,4}in the\b/.test(
    lower.slice(last.index, at + 12)
  )
}

function clockTimeIsMonitoring(lower: string, at: number): boolean {
  const verbs = [
    ...lower.slice(0, at).matchAll(/\b(?:check|measure|monitor|record)\b/g),
  ]
  const last = verbs.at(-1)
  if (last?.index == null) {
    return false
  }
  const afterVerb = lower.slice(last.index, at)
  return !/\b(?:tablets?|tabs?|capsules?|caps?|pills?|drops?|puffs?|sprays?|patches?|take|give|inject|apply|inhale|instill)\b/.test(
    afterVerb
  )
}

function eveningAndBedtimeAreOneDose(lower: string): boolean {
  const eveningAt = lower.search(
    /\b(?:qpm|every evening|in the evening|evening)\b/
  )
  const bedtimeAt = lower.search(
    /\b(?:at bedtime|qhs|nightly|at night(?:time)?)\b/
  )
  if (eveningAt < 0 || bedtimeAt < 0) {
    return false
  }
  const between = lower.slice(
    Math.min(eveningAt, bedtimeAt),
    Math.max(eveningAt, bedtimeAt)
  )
  // "evening and at bedtime" and "every evening, at bedtime" are two
  // administrations. "every evening at bedtime" is one dose.
  return !/\band\b|,/.test(between)
}

// "the morning of surgery" and "the night before surgery" are when a
// one-time dose is given, not a recurring time of day.
function timeIsRelativeToEvent(lower: string): boolean {
  return /\b(?:morning|evening|afternoon|night|bedtime|noon)\s+(?:of|before|after)\b/.test(
    lower
  )
}

// "daily on weekdays and twice daily on weekends" has no single rate.
function hasConditionalFrequencies(lower: string): boolean {
  const withoutCompound = lower.replace(
    /\b(?:twice|two times|three times|four times|\d+\s+times)\s+(?:(?:a|per)\s+)?(?:day|daily)\b|\b(?:bid|tid|qid)\b/g,
    ' '
  )
  const standaloneDaily = /\b(?:daily|once daily|every day|each day|qd)\b/.test(
    withoutCompound
  )
  const otherDailyRate =
    /\b(?:twice\s+(?:(?:a|per)\s+)?(?:day|daily)|bid|tid|qid|(?:two|three|four|\d+)\s+times\s+(?:(?:a|per)\s+)?(?:day|daily))\b/.test(
      lower
    )
  return standaloneDaily && otherDailyRate
}

// Noon, and a meal we do not label, are real administrations. Paired with
// another time, showing only the parsed half would drop part of the schedule.
function mealIsNotADose(lower: string, at: number): boolean {
  const cues = [
    ...lower
      .slice(0, at)
      .matchAll(/\b(?:check|measure|monitor|record|avoid|not|never)\b/g),
  ]
  const last = cues.at(-1)
  if (last?.index == null) {
    return false
  }
  const afterCue = lower.slice(last.index, at)
  return !/\b(?:tablets?|tabs?|capsules?|caps?|pills?|drops?|puffs?|sprays?|patches?|take|give|inject|apply|inhale|instill)\b/.test(
    afterCue
  )
}

function mealAdministrations(lower: string): number {
  const found = new Set<string>()
  const patterns: [RegExp, string][] = [
    [/\bbreakfast\b/g, 'breakfast'],
    [/\blunch\b/g, 'lunch'],
    [/\b(?:dinner|supper)\b/g, 'dinner'],
  ]
  for (const [pattern, label] of patterns) {
    for (const match of lower.matchAll(pattern)) {
      if (match.index == null || mealIsNotADose(lower, match.index)) {
        continue
      }
      found.add(label)
    }
  }
  return found.size
}

// "every morning and every other evening" is a daily dose plus a QOD dose.
function everyOtherSplitsFromDailyDose(lower: string): boolean {
  if (!/\bevery other\b|\bqod\b/.test(lower)) {
    return false
  }
  const rest = lower.replace(
    /\bevery other (?:day|morning|evening|night)\b|\bqod\b/g,
    ' '
  )
  return matchTimes(rest).length > 0
}

function hasUnparsedCoordinatedTime(lower: string): boolean {
  // A comma lists times the same way "and" does: "morning, noon, bedtime".
  if (!/\band\b|\bor\b|,/.test(lower)) {
    return false
  }
  if (/\b(?:noon|midday|afternoon)\b/.test(lower)) {
    return true
  }
  // "every morning and at 8 PM" is a second administration. The numeric
  // clock is not a named time, so keeping QAM would drop the evening dose.
  if (
    matchTimes(lower).length > 0 &&
    /\b\d{1,2}(?::\d{2})?\s*(?:a\.?m\.?|p\.?m\.?)\b/.test(lower)
  ) {
    return true
  }
  return (
    /\b(?:meals?|breakfast|lunch|dinner|supper)\b/.test(lower) &&
    matchTimes(lower).length > 0
  )
}

// "daily on weekdays" and "every 8 hours on weekends" are not seven-day
// dosing. The one-line label has no place for the days, so any frequency
// is declined rather than shown as an everyday schedule.
const DAY_RESTRICTION =
  /\b(?:weekdays?|weekends?|mondays?|tuesdays?|wednesdays?|thursdays?|fridays?|saturdays?|sundays?|mon|tue(?:s)?|wed|thu(?:rs)?|fri|sat|sun)\b/

// "May take 1 additional tablet as needed" and "daily, may repeat once as
// needed" are rescue doses. A comma can also be the sig's own PRN tail
// ("every 6 hours, as needed for pain"), which is not a second dose. A later
// clause is rescue only when it has its own dose or a rescue cue. "1,000"
// has no space after the comma, so it stays one amount. A semicolon splits
// even without a following space.
function hasRescueClause(lower: string): boolean {
  if (
    /\b(?:additional|extra|rescue)\b/.test(lower) &&
    isDoseSummaryPrn(lower)
  ) {
    return true
  }
  const sentences = lower
    .split(/[.,]\s+|;+/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence.length > 0)
  if (sentences.length < 2) {
    return false
  }
  const [first, ...rest] = sentences
  return (
    first != null &&
    !isDoseSummaryPrn(first) &&
    rest.some((sentence) => isRescueClause(sentence, first))
  )
}

function isRescueClause(sentence: string, standing: string): boolean {
  // "may take at bedtime" is an optional second dose even with no PRN cue.
  // It stays off the shared PRN predicate.
  if (/\bmay take\b/.test(sentence)) {
    return true
  }
  if (!isDoseSummaryPrn(sentence)) {
    return false
  }
  if (/\b(?:additional|extra|rescue|repeat)\b/.test(sentence)) {
    return true
  }
  if (matchTimes(sentence).length > 0) {
    return true
  }
  if (
    doseCandidates(sentence).some(
      (candidate) => !candidate.truncated && !STRENGTH_FORMS.has(candidate.form)
    )
  ) {
    return true
  }
  // "once daily, twice daily if needed" is a second schedule. "every 6
  // hours, as needed for pain" is the same schedule's PRN tail.
  return matchRate(sentence) != null && matchRate(standing) != null
}

// "every morning and at bedtime as needed" is a rescue joined by "and",
// not a twice-daily PRN schedule. The standing morning dose stays unlabeled
// rather than becoming PRN. "if needed" stays off the shared PRN predicate.
function hasAndJoinedPrnTime(lower: string): boolean {
  const match =
    /\band\b([^.;]{0,80}?)\b(?:as needed|as-needed|prn|if[- ]needed)\b/.exec(
      lower
    )
  if (match?.index == null) {
    return false
  }
  const between = match[1] ?? ''
  if (
    !/\b(?:morning|evening|night(?:time)?|bedtime|qam|qpm|qhs|nightly)\b/.test(
      between
    )
  ) {
    return false
  }
  return hasSchedule(lower.slice(0, match.index))
}

// "with an additional tablet" and "daily plus 1 tablet" are a second
// dose even when they never say as needed. Showing only bedtime would
// drop the standing dose.
function hasAddedDose(lower: string): boolean {
  return /\bwith\s+an?\s+(?:additional|extra)\b|\ban?\s+(?:additional|extra)\s+(?:tablets?|tabs?|capsules?|caps?|pills?|doses?)\b|\bplus\s+(?:\d+\s+)?(?:tablets?|tabs?|capsules?|caps?|pills?|doses?)\b/.test(
    lower
  )
}

const CONCENTRATION =
  /\d+(?:\.\d+)?\s*(?:mg|units?)\s*(?:\/|\s+per)\s*(?:\d+(?:\.\d+)?\s*)?(?:m(?:l|illiliters?)|g)\b/g

function hasConcentration(lower: string): boolean {
  return new RegExp(CONCENTRATION.source).test(lower)
}

// En dash, em dash, and the other Unicode dashes are the same range
// separator as "-". A copied sig ("1–2 tablets 1–2 times daily") must not
// resume at each upper bound.
function normalizeDashes(text: string): string {
  return text.replace(/[\u2010-\u2015\u2212]/g, '-')
}

function prepareDosingText(lower: string): string {
  return withoutEmbeddedStrength(
    withoutCeilings(normalizeDashes(spellOutHalf(spellOutMixedHalf(lower))))
  )
    .replace(
      /\bfor\s+(?:(?:twice|two times)(?:[-\s]+(?:per|a))?[-\s]+(?:week|weekly|month|monthly)|(?:three|four) times (?:(?:per |a )?(?:week|weekly|month|monthly)))(?=\s+[a-z])/g,
      'for'
    )
    .replace(new RegExp(CONCENTRATION.source, 'g'), ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

interface WholeWordNumbers {
  [word: string]: string
}

const WHOLE_WORDS: WholeWordNumbers = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
  eleven: '11',
  twelve: '12',
}

// "1 and a half tablets" is 1.5, not the trailing half. A lone "half
// tablet" is still 0.5. Rewriting the mixed number first keeps the whole.
function spellOutMixedHalf(lower: string): string {
  return lower.replace(
    /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+and\s+(?:an?\s+|one[-\s])?half\s+(?=(?:tablets?|tabs?|capsules?|caps?|pills?)\b)/g,
    (match, whole: string) => {
      const amount = /^\d+$/.test(whole) ? whole : WHOLE_WORDS[whole]
      return amount == null ? match : `${amount}.5 `
    }
  )
}

// "half tablet", "a half tablet", and "one-half tablet" are 0.5, same as
// "0.5 tablet". "half of 1 tablet" is that same half, not the trailing 1.
// Leaving the word out makes two administrations look equal.
function spellOutHalf(lower: string): string {
  return lower
    .replace(
      /\b(?:a\s+half|one[-\s]half|half)\s+of\s+(?:1|one|a|an)\s+(?=(?:tablets?|tabs?|capsules?|caps?|pills?)\b)/g,
      '0.5 '
    )
    .replace(
      /\b(?:a\s+half|one[-\s]half|half)\s+(?=(?:tablets?|tabs?|capsules?|caps?|pills?)\b)/g,
      '0.5 '
    )
}

// "(5 mg)" and "of a 10 mg" name the product, not the amount taken.
// "2 of the 10 mg tablets" is still two tablets. The strength stays off
// the dose.
function withoutEmbeddedStrength(lower: string): string {
  const counted = lower
    .replace(
      /\b(\d+)\s*x\s*\d+(?:\.\d+)?\s*(?:mg|mcg|µg|ug|g|ml|milligrams?|micrograms?|grams?|milliliters?)\s+(tablets?|tabs?|pills?|capsules?|caps?)\b/g,
      '$1 $2'
    )
    .replace(
      /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+of\s+(?:the\s+)?\d+(?:\.\d+)?\s*(?:mg|mcg|µg|ug|g|ml|milligrams?|micrograms?|grams?|milliliters?)\s+(tablets?|tabs?|pills?|capsules?|caps?)\b/g,
      (match, word: string, form: string) => {
        const amount = /^\d+$/.test(word) ? word : WHOLE_WORDS[word]
        return amount == null ? match : `${amount} ${form}`
      }
    )
    .replace(
      /\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+\d+(?:\.\d+)?\s*(?:mg|mcg|µg|ug|g|ml|milligrams?|micrograms?|grams?|milliliters?)\s+(tablets?|tabs?|pills?|capsules?|caps?)\b/g,
      (match, word: string, form: string) => {
        const amount = WHOLE_WORDS[word]
        return amount == null ? match : `${amount} ${form}`
      }
    )
  return counted
    .replace(
      /\(\s*\d+(?:\.\d+)?\s*(?:mg|mcg|µg|ug|g|ml|milligrams?|micrograms?|grams?|milliliters?|units?)\s*\)/g,
      ' '
    )
    .replace(
      /\s+of\s+(?:a|an)\s+\d+(?:\.\d+)?\s*(?:mg|mcg|µg|ug|g|ml|milligrams?|micrograms?|grams?|milliliters?|units?)\b\s*/g,
      ' '
    )
}

interface DailyCountWords {
  [word: string]: number
}

const DAILY_COUNT_WORDS: DailyCountWords = {
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
}

// Only 1–4 times a day have a label. "5 times daily" and "5x daily" must not
// fall through to the bare "daily" pattern and display as QD.
const DAILY_COUNT =
  /\b(\d+|five|six|seven|eight|nine|ten|eleven|twelve)\s+times?\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b|\b(\d+)\s*x\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b/g

function unrecognizedDailyCount(lower: string): boolean {
  for (const match of lower.matchAll(new RegExp(DAILY_COUNT.source, 'g'))) {
    const raw = match[1] ?? match[2] ?? ''
    const count = /^\d+$/.test(raw) ? Number(raw) : DAILY_COUNT_WORDS[raw]
    if (count == null || count < 1 || count > 4) {
      return true
    }
  }
  return false
}

// "morning or bedtime" is a choice. "not in the morning" removes a time.
// Neither is two administrations.
function clockTimesAreNotASchedule(lower: string): boolean {
  if (
    /\bnot\s+(?:[a-z]+\s+){0,3}(?:(?:in the|every)\s+)?(?:morning|evening|night(?:time)?|bedtime)\b/.test(
      lower
    ) ||
    /\bnot\s+(?:qam|qpm|qhs|nightly)\b/.test(lower) ||
    /\bnever\s+(?:at\s+|in the\s+|every\s+)?(?:morning|evening|night(?:time)?|bedtime|qam|qpm|qhs|nightly)\b/.test(
      lower
    ) ||
    /\bno\s+dose\s+(?:at\s+)?(?:morning|evening|night(?:time)?|bedtime)\b/.test(
      lower
    )
  ) {
    return true
  }
  return /\b(?:morning|evening|bedtime|night|qam|qpm|qhs|nightly)\b[^.]{0,40}\bor\b[^.]{0,40}\b(?:morning|evening|bedtime|night|qam|qpm|qhs|nightly)\b/.test(
    lower
  )
}

// "every 6 hours or every 8 hours" is not Q6H. Two different hourly, weekly,
// or monthly rates have no single abbreviation. A longer phrase is claimed
// first so "twice weekly" is not also counted as "weekly".
function hasCompetingIntervalRates(lower: string): boolean {
  const claimed = new Array<boolean>(lower.length).fill(false)
  const labels = new Set<string>()
  const consider = (
    pattern: RegExp,
    labelOf: (match: RegExpMatchArray) => string
  ) => {
    for (const match of lower.matchAll(new RegExp(pattern.source, 'g'))) {
      const start = match.index ?? 0
      const end = start + match[0].length
      if (claimed.slice(start, end).some(Boolean)) {
        continue
      }
      const label = labelOf(match)
      if (!label) {
        continue
      }
      claimed.fill(true, start, end)
      labels.add(label)
    }
  }
  consider(/\bevery (\d+) hours?\b/, (match) => `Q${match[1]}H`)
  consider(/\bevery other (?:day|morning|evening|night)\b|\bqod\b/, () => 'QOD')
  consider(/\bevery (\d+) days?\b/, (match) => `Q${match[1]}D`)
  consider(/\bq\s*(\d+)\s*d(?:ays?)?\b/, (match) => {
    const days = Number(match[1])
    return days > 0 ? `Q${days}D` : ''
  })
  consider(/\bq\s*(\d+)\s*h(?:ours?|rs?)?\b/, (match) => {
    const hours = Number(match[1])
    return hours > 0 ? `Q${hours}H` : ''
  })
  consider(
    /\b(?:twice|two times)(?:[-\s]+(?:per|a))?[-\s]+(?:week|weekly)\b/,
    () => '2x/week'
  )
  consider(/\bthree times (?:(?:per |a )?(?:week|weekly))\b/, () => '3x/week')
  consider(/\bfour times (?:(?:per |a )?(?:week|weekly))\b/, () => '4x/week')
  consider(
    /(?<![\d-])(\d+) times (?:per |a )?week(?:ly)?\b/,
    (match) => `${match[1]}x/week`
  )
  consider(/\bbiw\b/, () => 'BIW')
  consider(/\btiw\b/, () => 'TIW')
  consider(/\bqw\b/, () => 'QW')
  consider(/\bweekly\b|\bonce (?:per|a) week\b/, () => 'weekly')
  consider(
    /\b(?:twice|two times)(?:[-\s]+(?:per|a))?[-\s]+(?:month|monthly)\b/,
    () => '2x/month'
  )
  consider(
    /\bthree times (?:(?:per |a )?(?:month|monthly))\b/,
    () => '3x/month'
  )
  consider(/\bfour times (?:(?:per |a )?(?:month|monthly))\b/, () => '4x/month')
  consider(
    /(?<![\d-])(\d+)\s+times?\s+(?:(?:per|a)\s+)?month(?:ly)?\b/,
    (match) => `${match[1]}x/month`
  )
  consider(/\bmonthly\b|\bonce (?:per|a) month\b/, () => 'monthly')
  return labels.size > 1
}

// "BID or TID" is a choice. The first match must not become the schedule.
function hasCompetingDailyRates(lower: string): boolean {
  const labels = new Set<string>()
  if (
    /\bbid\b|\btwice(?:[-\s]+a)?[-\s]+(?:day|daily)\b|(?<![\d-])(?:2x|(?:2|two) times)\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b/.test(
      lower
    )
  ) {
    labels.add('BID')
  }
  if (
    /\btid\b|(?<![\d-])(?:3x|(?:3|three) times)\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b/.test(
      lower
    )
  ) {
    labels.add('TID')
  }
  if (
    /\bqid\b|(?<![\d-])(?:4x|(?:4|four) times)\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b/.test(
      lower
    )
  ) {
    labels.add('QID')
  }
  return labels.size > 1
}

// "every 6 hours or twice daily", "daily or every 2 days", "daily or
// weekly", and "every 6 hours or every 2 days" name two schedules. A course
// length ("for a week") is not one of those rates. Two families are not one
// regimen.
const LONGER_PERIOD_RATE =
  /\bevery\s+\d+\s+days?\b|\bq\s*\d+\s*d(?:ays?)?\b|\bevery other (?:day|morning|evening|night)\b|\bqod\b|\b(?:twice|two times)(?:[-\s]+(?:per|a))?[-\s]+(?:week|weekly)\b|\bthree times (?:(?:per |a )?(?:week|weekly))\b|\bfour times (?:(?:per |a )?(?:week|weekly))\b|(?<![\d-])\d+\s+times (?:per |a )?week(?:ly)?\b|(?<![\d-])\d+x\s+(?:(?:per|a)\s+)?(?:week|weekly)\b|\bweekly\b|\bonce (?:per|a) week\b|\bbiw\b|\btiw\b|\bqw\b|\b(?:twice|two times)(?:[-\s]+(?:per|a))?[-\s]+(?:month|monthly)\b|\bthree times (?:(?:per |a )?(?:month|monthly))\b|\bfour times (?:(?:per |a )?(?:month|monthly))\b|(?<![\d-])\d+\s+times?\s+(?:(?:per|a)\s+)?month(?:ly)?\b|(?<![\d-])\d+x\s+(?:(?:per|a)\s+)?(?:month|monthly)\b|\bmonthly\b|\bonce (?:per|a) month\b/

function hasCrossFamilyRates(lower: string): boolean {
  const hourly = /\bevery\s+\d+\s+hours?\b|\bq\s*\d+\s*h(?:ours?|rs?)?\b/.test(
    lower
  )
  const daily =
    DAILY_RATE.test(lower) ||
    /\bbid\b|\btid\b|\bqid\b|\btwice(?:[-\s]+a)?[-\s]+(?:day|daily)\b|(?<![\d-])[234]x\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b|(?<![\d-])(?:[234]|two|three|four)\s+times\s+(?:(?:per|a|every)\s+)?(?:day|daily)\b/.test(
      lower
    )
  const longer = LONGER_PERIOD_RATE.test(lower)
  return [hourly, daily, longer].filter(Boolean).length > 1
}

// "twice daily and 1 tablet at bedtime" is not BID. An hourly, weekly, or
// multi-day rate plus a separate clock-time dose is not that rate either.
// "daily at bedtime" still names that one dose.
function explicitRateOmitsAnotherDose(lower: string): boolean {
  const rate = matchRate(lower)
  if (rate == null || rate === 'QD' || TIME_LABELS.has(rate)) {
    return false
  }
  if (matchTimes(lower).length === 0) {
    return false
  }
  if (rate === 'BID' || rate === 'TID' || rate === 'QID') {
    return true
  }
  return separateClockDose(lower)
}

function separateClockDose(lower: string): boolean {
  const rateAt = lower.search(
    /\bevery\s+\d+\s+(?:hours?|days?)\b|\bq\s*\d+\s*h(?:ours?|rs?)?\b|\b(?:weekly|monthly|biw|tiw|qw)\b|\b(?:twice|two times|three times|four times)(?:[-\s]+(?:per|a))?[-\s]+(?:week|weekly|month|monthly)\b/
  )
  const clockAt = lower.search(
    /\b(?:at bedtime|qhs|before beds?|before bedtime|at nighttime|at night|every night|in the morning|every morning|qam|in the evening|every evening|qpm)\b/
  )
  if (rateAt < 0 || clockAt < 0) {
    return false
  }
  const between = lower.slice(
    Math.min(rateAt, clockAt),
    Math.max(rateAt, clockAt)
  )
  if (/\band\b/.test(between)) {
    return true
  }
  return (
    /,/.test(between) &&
    doseCandidates(between).some(
      (candidate) => !candidate.truncated && !STRENGTH_FORMS.has(candidate.form)
    )
  )
}

function clockTimeAddsADose(lower: string): boolean {
  const dailyAt = lower.search(/\b(?:once daily|every day|qd|daily)\b/)
  const clockAt = lower.search(
    /\b(?:qam|every morning|each morning|in the morning|qpm|every evening|each evening|in the evening|at bedtime|qhs|nightly|before beds?|before bedtime|at nighttime|at night|every night|each night|in the night)\b/
  )
  if (dailyAt < 0 || clockAt < 0) {
    return false
  }
  const between = lower.slice(
    Math.min(dailyAt, clockAt),
    Math.max(dailyAt, clockAt)
  )
  if (/\b(?:and|or)\b/.test(between)) {
    return true
  }
  return (
    /,/.test(between) &&
    doseCandidates(between).some(
      (candidate) => !candidate.truncated && !STRENGTH_FORMS.has(candidate.form)
    )
  )
}

function numericClockCount(lower: string): number {
  const keys = new Set<string>()
  for (const match of lower.matchAll(
    /\b(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)\b/g
  )) {
    const hour = Number(match[1])
    if (hour < 1 || hour > 12) {
      continue
    }
    const minute = match[2] ?? '00'
    const meridiem = (match[3] ?? '').replace(/\./g, '')
    keys.add(`${hour}:${minute}${meridiem}`)
  }
  return keys.size
}

function frequencyFromTimes(count: number): string | undefined {
  if (count === 2) {
    return 'BID'
  }
  if (count === 3) {
    return 'TID'
  }
  if (count >= 4) {
    return 'QID'
  }
  return undefined
}

// A single clock time names itself ("daily at bedtime" is QHS). Two or more
// distinct clock times are the schedule, so morning plus bedtime is BID
// rather than whichever phrase the list happens to try first.
function resolveFrequency(lower: string): string | undefined {
  const rate = matchRate(lower)
  const times = matchTimes(lower)
  // "if missed, take it in the evening" is a fallback, not a second dose.
  // "while awake" is not an around-the-clock interval, and the label has
  // no place to keep that limit.
  if (
    DAY_RESTRICTION.test(lower) ||
    // "on days 1, 8, and 15 monthly" is a list of days, not once monthly.
    /\bdays?\s+\d+\s*,\s*\d+/.test(lower) ||
    // "every 6 hours 3 days a week" is not an around-the-clock rate.
    /\b(?:\d+|one|two|three|four|five|six|seven)\s+days?\s+(?:a|per)\s+week\b/.test(
      lower
    ) ||
    /\bif missed\b/.test(lower) ||
    /\bwhile awake\b/.test(lower) ||
    /\bduring the day\b/.test(lower) ||
    /\bduring waking hours\b/.test(lower) ||
    /\bwhen awake\b/.test(lower)
  ) {
    return undefined
  }
  if (
    hasConditionalFrequencies(lower) ||
    hasCompetingIntervalRates(lower) ||
    hasCompetingDailyRates(lower) ||
    hasCrossFamilyRates(lower) ||
    explicitRateOmitsAnotherDose(lower) ||
    everyOtherSplitsFromDailyDose(lower)
  ) {
    return undefined
  }
  if (timeIsRelativeToEvent(lower)) {
    if (rate && rate !== 'QD' && !TIME_LABELS.has(rate)) {
      return rate
    }
    return undefined
  }
  if (unrecognizedDailyCount(lower)) {
    if (
      rate == null ||
      rate === 'QD' ||
      RANGED_RATES.has(rate) ||
      TIME_LABELS.has(rate)
    ) {
      return undefined
    }
    return rate
  }
  if (
    unrecognizedPeriodCount(lower) &&
    (rate == null || rate === 'weekly' || rate === 'monthly')
  ) {
    return undefined
  }
  if (clockTimesAreNotASchedule(lower) || hasUnparsedCoordinatedTime(lower)) {
    if (rate && rate !== 'QD' && !TIME_LABELS.has(rate)) {
      return rate
    }
    return undefined
  }
  // "do not take daily" and "never daily" are exclusions, not a QD regimen.
  if (
    rate === 'QD' &&
    /\b(?:not|never)\s+(?:[a-z]+\s+){0,4}(?:daily|every day|once daily|each day|qd)\b/.test(
      lower
    )
  ) {
    return times.length === 1 ? times[0] : undefined
  }
  const fromTimes = frequencyFromTimes(times.length)
  // "at 8 AM and 8 PM daily" is two doses. The bare daily rate must not
  // hide the evening one. "8 AM or 8 PM" is a choice, not both.
  const fromClocks = frequencyFromTimes(numericClockCount(lower))
  if (
    fromClocks &&
    times.length === 0 &&
    (rate == null || rate === 'QD' || rate === 'once')
  ) {
    return /\b\d{1,2}(?::\d{2})?\s*(?:a\.?m\.?|p\.?m\.?)\b[^.]{0,40}\bor\b[^.]{0,40}\b\d{1,2}(?::\d{2})?\s*(?:a\.?m\.?|p\.?m\.?)\b/.test(
      lower
    )
      ? undefined
      : fromClocks
  }
  // Two clock times are the schedule. An isolated "one time" must not
  // hide morning and evening as a single dose.
  if (
    fromTimes &&
    (rate == null || rate === 'QD' || rate === 'once' || TIME_LABELS.has(rate))
  ) {
    return fromTimes
  }
  if (rate === 'QD' && times.length === 1) {
    return clockTimeAddsADose(lower) ? undefined : times[0]
  }
  // Breakfast and dinner are two administrations. "every day" does not
  // collapse them into one daily dose.
  const fromMeals = frequencyFromTimes(mealAdministrations(lower))
  if (
    fromMeals &&
    (rate == null || rate === 'QD' || rate === 'once') &&
    times.length === 0
  ) {
    // "breakfast or dinner" is a choice. "between breakfast and dinner"
    // is one window. Neither is two administrations.
    return /\b(?:breakfast|lunch|dinner|supper)\b[^.]{0,40}\bor\b[^.]{0,40}\b(?:breakfast|lunch|dinner|supper)\b/.test(
      lower
    ) ||
      /\bbetween\s+(?:breakfast|lunch|dinner|supper)\s+and\s+(?:breakfast|lunch|dinner|supper)\b/.test(
        lower
      )
      ? undefined
      : fromMeals
  }
  return rate ?? (times.length === 1 ? times[0] : undefined)
}

interface ReadAmount {
  amount?: string
  form?: string
  // The match is only the tail of "2 3 tablets" or "1,000 units": a digit
  // and whitespace, or a digit and comma, sit immediately before it, and it
  // is not a mixed number or a grouped amount. Showing that tail understates
  // the dose.
  truncated: boolean
}

function boundedPeriodCount(
  raw: string | undefined,
  period: 'week' | 'month'
): string {
  const count = Number(raw)
  return count >= 1 && count <= 4 ? `${count}x/${period}` : ''
}

function unrecognizedPeriodCount(lower: string): boolean {
  for (const match of lower.matchAll(
    /\b(\d+)x\s+(?:(?:per|a)\s+)?(?:week(?:ly)?|month(?:ly)?)\b/g
  )) {
    const count = Number(match[1])
    if (!Number.isFinite(count) || count < 1 || count > 4) {
      return true
    }
  }
  return false
}

function amountMatches(lower: string): RegExpMatchArray[] {
  return [...lower.matchAll(new RegExp(AMOUNT_AND_FORM.source, 'g'))]
}

function normalizeAmount(raw: string): string {
  // "1 or 1/2" is a choice, not the mixed number "1 1/2".
  const integerOrFraction = raw.match(/^(\d+)\s+or\s+(\d+\/\d+)$/)
  if (integerOrFraction) {
    return `${integerOrFraction[1]} or ${integerOrFraction[2]}`
  }
  // Normalize "1 - 2" / "1 to 2" and "1 and 1/2", and give a bare leading
  // dot its zero back.
  return raw
    .replace(/,/g, '')
    .replace(/\s*(?:-|to|or)\s*/, '-')
    .replace(/^\./, '0.')
    .replace(/^(\d+)(?:\s+(?:and\s+)?|\s*-\s*)(\d+\/\d+)$/, '$1 $2')
}

interface DoseCandidate {
  amount: string
  form: string
  truncated: boolean
}

function doseCandidates(lower: string): DoseCandidate[] {
  const candidates: DoseCandidate[] = []
  for (const match of amountMatches(lower)) {
    if (match.index == null) {
      continue
    }
    const abbreviation = FORM_ABBREVIATIONS.find(([pattern]) =>
      pattern.test(match[2])
    )
    if (!abbreviation) {
      continue
    }
    const formEnd = match.index + match[0].length
    // "5 units/kg" and "10 mg per kg" are not fixed doses. "10 mg/kg/day"
    // is a daily total, so dropping "/day" would publish that total as the
    // dose given twice.
    const tail = lower.slice(formEnd)
    const perKgDay = /^\s*(?:\/|\bper)\s*kg\s*(?:\/|\bper)\s*day\b/.test(tail)
    const perKg = /^\s*(?:\/|\bper)\s*kg\b/.test(tail)
    const surfaceDay =
      /^\s*(?:\/|\bper)\s*(m2|m²)\s*(?:\/|\bper)\s*day(?!\w)/.exec(tail)
    const surface = /^\s*(?:\/|\bper)\s*(m2|m²)(?!\w)/.exec(tail)
    const suffix = surfaceDay
      ? `/${surfaceDay[1]}/day`
      : surface
        ? `/${surface[1]}`
        : perKgDay
          ? '/kg/day'
          : perKg
            ? '/kg'
            : ''
    candidates.push({
      amount: normalizeAmount(match[1]),
      form: `${abbreviation[1]}${suffix}`,
      truncated:
        /\d\s+$/.test(lower.slice(0, match.index)) ||
        /\d,\s*$/.test(lower.slice(0, match.index)) ||
        /\d-\s*$/.test(lower.slice(0, match.index)),
    })
  }
  return candidates
}

const KNOWN_FORM_WORD =
  /^(?:tabs?|tablets?|pills?|capsules?|caps?|gtts?|drops?|suppositories|suppository|patches|patch|applications?|puffs?|sprays?|inhalations?|lozenges?|scoops?|packets?|units?|ml|milliliters?|mg|milligrams?|grams?|g)$/

const NON_FORM_WORD =
  /^(?:po|orally|by|mouth|daily|day|days|times?|every|hours?|hrs?|minutes?|mins?|seconds?|secs?|weekly|weeks?|monthly|months?|morning|evening|night|nights|bedtime|noon|with|food|meals|for|once|twice|needed|as|and|or|to|take|give|before|after|surgery|procedure|weekdays?|weekends?|on|at|the|a|per|qam|qpm|qhs|qd|bid|tid|qid|biw|tiw|qw|prn|additional|may|not|more|than|do|exceed|maximum|in|of|each|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)$/

function hasUnrecognizedDoseForm(lower: string): boolean {
  const formAfterNumber =
    /(?:\d+(?:\.\d+)?|\.\d+)(?:\/\d+)?(?:\s*(?:-|to|and)\s*\d+(?:\.\d+)?)?\s+([a-z]+)/g
  // "two vials" is the same unknown form as "2 vials". The 10 mg after it
  // is the product strength, not the amount given.
  const formAfterWordedCount =
    /\b(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+([a-z]+)/g
  for (const pattern of [formAfterNumber, formAfterWordedCount]) {
    for (const match of lower.matchAll(pattern)) {
      const word = match[1] ?? ''
      if (KNOWN_FORM_WORD.test(word) || NON_FORM_WORD.test(word)) {
        continue
      }
      return true
    }
  }
  return false
}

function readAmount(lower: string): ReadAmount {
  const candidates = doseCandidates(lower)
  if (candidates.length === 0) {
    return { truncated: false }
  }
  const doses = candidates.filter(
    (candidate) => !STRENGTH_FORMS.has(candidate.form)
  )
  const preferred = doses.find((candidate) => !candidate.truncated)
  if (!preferred && doses.some((candidate) => candidate.truncated)) {
    return { truncated: true }
  }
  // "2 squirts" is a dose form we do not know. A nearby "10 mg" is the
  // strength, not that dose.
  if (!preferred && hasUnrecognizedDoseForm(lower)) {
    return { truncated: false }
  }
  const chosen =
    preferred ?? candidates.find((candidate) => !candidate.truncated)
  if (!chosen) {
    return { truncated: true }
  }
  return { amount: chosen.amount, form: chosen.form, truncated: false }
}

// "twice daily; take 1 tablet at bedtime" is a second administration. The
// amounts match, so a count comparison would keep BID and drop bedtime.
function hasSeparateDoseClause(lower: string): boolean {
  const clauses = lower
    .split(/[.;]\s+|;+/)
    .map((clause) => clause.trim())
    .filter((clause) => clause.length > 0)
  if (clauses.length < 2) {
    return false
  }
  const withDose = clauses.filter((clause) =>
    doseCandidates(clause).some(
      (candidate) => !candidate.truncated && !STRENGTH_FORMS.has(candidate.form)
    )
  )
  return withDose.length > 1
}

// "1 tablet in the morning and 2 at night" repeats the dose without the
// unit. "and half at night" is a different amount. Treating the clock
// times as BID would publish two equal tablets.
function hasElidedCoordinatedDose(lower: string): boolean {
  const hasFormedDose = doseCandidates(lower).some(
    (candidate) => !candidate.truncated && !STRENGTH_FORMS.has(candidate.form)
  )
  if (!hasFormedDose) {
    return false
  }
  return /\b(?:and|,)\s+(?:\d*\.\d+|\d+\/\d+|\d+|one|two|three|four|a\s+half|one[-\s]half|half)\s+(?:at|in the|every|before|after|qam|qpm|qhs|night|morning|evening|bedtime)\b/.test(
    lower
  )
}

// Morning tablet and bedtime tablets of a different count are not one dose.
function hasConflictingDoses(lower: string): boolean {
  const doses = doseCandidates(lower).filter(
    (candidate) => !candidate.truncated
  )
  const administered = doses.filter(
    (candidate) => !STRENGTH_FORMS.has(candidate.form)
  )
  // mg/mL are the dose when no tablet or capsule is present. 5 mg and 10 mg
  // are two doses, not a strength plus a form.
  const compared = administered.length > 0 ? administered : doses
  const amounts = new Set<string>()
  for (const key of [...compared.map(doseKey), ...wordedDoseKeys(lower)]) {
    amounts.add(key)
    if (amounts.size > 1) {
      return true
    }
  }
  return false
}

function doseKey(candidate: DoseCandidate): string {
  return `${candidate.form}:${candidate.amount}`
}

function wordedDoseKeys(lower: string): string[] {
  const keys: string[] = []
  const pattern =
    /\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+(tablets?|tabs?|pills?|capsules?|caps?|drops?|gtts?)\b/g
  for (const match of lower.matchAll(pattern)) {
    const word = match[1] ?? ''
    const formWord = match[2] ?? ''
    const amount = WHOLE_WORDS[word]
    const form = /^(?:capsules?|caps?)$/.test(formWord)
      ? 'cap'
      : /^(?:drops?|gtts?)$/.test(formWord)
        ? 'gtt'
        : 'tab'
    if (amount) {
      keys.push(`${form}:${amount}`)
    }
  }
  return keys
}

// "call doctor if pain persists" is an instruction, not a PRN dose.
// "every 6 hours if pain" still qualifies the dose.
function ifPainQualifiesTheDose(text: string): boolean {
  const match = /\bif pain\b/.exec(text)
  if (match?.index == null) {
    return false
  }
  const clauseStart = Math.max(
    text.lastIndexOf(';', match.index),
    text.lastIndexOf('.', match.index)
  )
  const clause =
    clauseStart < 0
      ? text.slice(0, match.index)
      : text.slice(clauseStart + 1, match.index)
  return !/\b(?:call|contact|notify|phone)\b/.test(clause)
}

// "daily if systolic blood pressure is above 160" is conditional. "if
// needed", "if pain", and "if missed" are not that kind of condition.
function hasConditionalDose(lower: string): boolean {
  const withoutKnownIf = lower
    .replace(/\bif[- ]needed\b/g, ' ')
    .replace(/\bif pain\b/g, ' ')
    .replace(/\bif missed\b/g, ' ')
  return /\bif\b/.test(withoutKnownIf)
}

// "if needed", "if pain", "as required", and "as necessary" are display
// cues only. The shared PRN predicate stays on "as needed" / PRN.
function isDoseSummaryPrn(text: string): boolean {
  // "not as needed" and "not PRN" cancel the cue. This stays in the
  // summary parser; the shared PRN check is unchanged.
  const withoutNegated = text.replace(
    /\b(?:not|never)\s+(?:[a-z]+\s+){0,3}(?:as[-\s](?:needed|required|necessary)|prn)\b/g,
    ' '
  )
  return (
    isPrnText(withoutNegated) ||
    /\bif[- ]needed\b/.test(withoutNegated) ||
    ifPainQualifiesTheDose(withoutNegated) ||
    /\bas[-\s](?:required|necessary)\b/.test(withoutNegated)
  )
}

export function summarizeSig(sigText: string | undefined): SigSummary {
  const lower = prepareDosingText(sigText?.toLowerCase() ?? '')
  const asNeeded = isDoseSummaryPrn(sigText?.toLowerCase() ?? '')
  const { amount, form, truncated } = readAmount(lower)

  return {
    amount: truncated ? undefined : amount,
    form: truncated ? undefined : form,
    frequency: truncated ? undefined : resolveFrequency(lower),
    asNeeded,
  }
}

function hasSchedule(lower: string): boolean {
  return matchRate(lower) != null || matchTimes(lower).length > 0
}

// A regimen that steps down ("2 tablets on the first day then 1 tablet
// daily", a steroid taper) has no single dose and frequency. Summarizing it
// would show the first phase as if it were the standing order, so those are
// declined. "then 7 days off" is a break in the cycle, not continuous daily.
// "then reduce" or "then taper" is a later phase even with no second dose.
// A procedural "then" ("swish, then spit") carries no second schedule and
// is left alone.
function hasSecondDosePhase(lower: string): boolean {
  const tail = lower
    .split(/\bthen\b/)
    .slice(1)
    .join(' ')
  return (
    tail !== '' &&
    (hasSchedule(tail) ||
      /\boff\b/.test(tail) ||
      /\b(?:taper|reduc(?:e|ed|ing)|decreas(?:e|ed|ing))\b/.test(tail))
  )
}

// "1 and a half" that survived as "1 and 0.5" would publish only the half.
function lostMixedHalf(lower: string): boolean {
  return /\b(?:\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+and\s+0\.5\b/.test(
    lower
  )
}

// "1 tab QD", "0.5 mL once", "1 gtt TID PRN" — or undefined when the sig
// yields neither a dose nor a frequency worth showing. An exception ("daily
// except 2 tablets on Sunday") and a truncated mixed number have no single
// dose to show.
export function formatSigSummary(
  sigText: string | undefined,
  options?: { asNeeded?: boolean; authoritativeAsNeeded?: boolean }
): string | undefined {
  const lower = normalizeDashes(sigText?.toLowerCase() ?? '')
  const dosing = prepareDosingText(lower)
  if (
    /\bexcept\b/.test(lower) ||
    hasSecondDosePhase(lower) ||
    lostMixedHalf(dosing) ||
    hasRescueClause(lower) ||
    hasConditionalDose(lower) ||
    hasAndJoinedPrnTime(lower) ||
    hasAddedDose(lower) ||
    // "one time; repeat after 7 days" is another dose with the amount
    // left off. "again" is the same. A lone "one time" has neither word.
    /\b(?:repeat|again)\b/.test(lower) ||
    hasConflictingDoses(dosing) ||
    hasElidedCoordinatedDose(dosing) ||
    hasSeparateDoseClause(dosing) ||
    readAmount(dosing).truncated ||
    (hasConcentration(lower) && !readAmount(dosing).amount)
  ) {
    return undefined
  }
  const { amount, form, frequency, asNeeded } = summarizeSig(sigText)
  const dose = amount && form ? `${amount} ${form}` : undefined
  // TQ1 decides PRN when it is present. Text "as needed" does not override
  // a scheduled priority, and a PRN priority still marks a scheduled sig.
  const prn =
    options?.authoritativeAsNeeded === undefined
      ? asNeeded || options?.asNeeded
      : options.authoritativeAsNeeded
  const parts = [dose, frequency, prn ? 'PRN' : undefined].filter(Boolean)
  // PRN alone says nothing about dose or schedule.
  if (!dose && !frequency) {
    return undefined
  }
  return parts.join(' ')
}
